import { useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RepoPromptPicker } from '@/components/projects/workflows/shared/RepoPromptPicker'
import { REPO_CONNECTOR_TYPES } from '@/components/projects/workflows/shared/repoConnectorTypes'
import {
  emptySelection,
  parseSelections,
  normalizeFolder,
  parseAccept,
  resolveDefaultConnector,
  selectionError,
} from '@/components/projects/workflows/shared/repoFilePickerUtils'
import type { TriggerInputField } from '@/types/workflowDefinition'
import type { ConnectorType } from '@/types'
import type { RepoPromptOverride } from '@/types/claude'

interface RepoFilePickerFieldProps {
  projectId: string
  field: TriggerInputField
  value: string
  onChange: (serialized: string) => void
  error?: string
}

interface SelectionRow {
  id: string
  selection: RepoPromptOverride
}

export function RepoFilePickerField({ projectId, field, value, onChange, error }: Readonly<RepoFilePickerFieldProps>) {
  const multiSelect = field.multiSelect === true
  const defaultConnector = resolveDefaultConnector(field)
  const nextId = useRef(0)
  const makeRow = (selection: RepoPromptOverride): SelectionRow => ({ id: `add-${nextId.current++}`, selection })
  const [rows, setRows] = useState<SelectionRow[]>(() => {
    const parsed = parseSelections(value)
    const initial = parsed.length > 0 ? parsed : [emptySelection(defaultConnector)]
    return initial.map((selection, index) => ({
      id: `init-${index}`,
      selection: { ...selection, connectorType: selection.connectorType || defaultConnector },
    }))
  })

  const connectorTypes = (field.connectorType ? [field.connectorType] : REPO_CONNECTOR_TYPES) as ConnectorType[]
  const label = field.label ?? 'Requirement document'
  const folder = normalizeFolder(field.folder)
  const acceptExtensions = parseAccept(field.accept)
  const invalidConnectorConfigured =
    !!field.connectorType && !(REPO_CONNECTOR_TYPES as readonly string[]).includes(field.connectorType)

  const commit = (next: SelectionRow[]) => {
    setRows(next)
    const meaningful = next.map(r => r.selection).filter(s => s.filePath || s.repository)
    if (meaningful.length === 0) {
      onChange('')
    } else if (multiSelect) {
      onChange(JSON.stringify(meaningful))
    } else {
      onChange(JSON.stringify(meaningful[0]))
    }
  }

  const updateRow = (id: string, selection: RepoPromptOverride) =>
    commit(rows.map(r => (r.id === id ? { ...r, selection } : r)))

  const addRow = () => commit([...rows, makeRow(emptySelection(defaultConnector))])

  const removeRow = (id: string) => {
    const next = rows.filter(r => r.id !== id)
    commit(next.length > 0 ? next : [makeRow(emptySelection(defaultConnector))])
  }

  return (
    <div className="space-y-2" data-testid={`trigger-input-${field.name}`}>
      <div className="text-xs font-medium">
        {label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      {field.description && <p className="text-xs text-muted-foreground">{field.description}</p>}

      {invalidConnectorConfigured ? (
        <p className="text-xs text-red-500" role="alert">
          Connector &quot;{field.connectorType}&quot; is not a source-control connector. Fix the workflow&apos;s{' '}
          <code>connectorType</code> (allowed: {REPO_CONNECTOR_TYPES.join(', ')}).
        </p>
      ) : (
      <>
      <div className="space-y-3">
        {rows.map((row, index) => {
          const rowError = selectionError(row.selection, folder, acceptExtensions)
          return (
            <div key={row.id} className="rounded-md border p-2">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <RepoPromptPicker
                    projectId={projectId}
                    connectorTypes={connectorTypes}
                    value={row.selection}
                    onChange={selection => updateRow(row.id, selection)}
                    testIdPrefix={`repo-file-${field.name}-${index}`}
                    folderPrefix={field.folder ?? undefined}
                    accept={field.accept ?? undefined}
                    fileLabel="Document"
                  />
                  {rowError && <p className="mt-1 text-xs text-red-500" role="alert">{rowError}</p>}
                </div>
                {multiSelect && rows.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mt-4 h-8 w-8 shrink-0"
                    aria-label={`Remove document ${index + 1}`}
                    data-testid={`repo-file-${field.name}-remove-${index}`}
                    onClick={() => removeRow(row.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {multiSelect && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1"
          data-testid={`repo-file-${field.name}-add`}
          onClick={addRow}
        >
          <Plus className="h-4 w-4" /> Add document
        </Button>
      )}
      </>
      )}

      {error && <p className="text-xs text-red-500" role="alert">{error}</p>}
    </div>
  )
}
